// useFloorOptions — PowerSync-backed hook for the contract-scoped
// `floor_options` picker config that drives the floor list inside the
// New Room dialog. The admin Templates → Floors tab CRUDs this table;
// field staff read it via this hook.
//
// All authenticated users receive `floor_options` through the
// `reference_global` sync stream (admin-curated reference data, not
// site-scoped). The same empty-state gate as `useRoomTypes` is used
// so a freshly installed device doesn't briefly render "no floors"
// before the first reference sync delivers them.
//
// Sort: display_order ASC (admin-controlled via the Templates → Floors
// drag-and-drop reorder UI, persisted by PUT /admin/floor-options/reorder),
// with created_at and name as deterministic tiebreaks for any rows
// that share the default display_order=0 before the first reorder.
// Field staff see the order admins set as soon as the
// reference_global stream resyncs.

import { useAllSyncStreamsHaveSynced, useQuery } from '@powersync/react'

import { db } from './client'

export interface UseFloorOptionsRow {
  id: string
  name: string
}

export interface UseFloorOptionsResult {
  floorOptions: UseFloorOptionsRow[]
  isLoading: boolean
  isFetching: boolean
  error: Error | undefined
}

interface FloorOptionSqlRow {
  id: string
  name: string
}

export function useFloorOptions(): UseFloorOptionsResult {
  const result = useQuery<FloorOptionSqlRow>(
    `SELECT id, name
     FROM floor_options
     ORDER BY display_order ASC, created_at ASC, name ASC`,
    [],
  )

  const referenceGlobalSynced = useAllSyncStreamsHaveSynced(db, [
    { name: 'reference_global', waitForStream: true },
  ])

  const isEmptyButUnsynced =
    result.data.length === 0 && !referenceGlobalSynced
  const isLoading = result.isLoading || isEmptyButUnsynced

  return {
    floorOptions: result.data.map((row) => ({ id: row.id, name: row.name })),
    isLoading,
    isFetching: result.isFetching,
    error: result.error,
  }
}
